/**
 * Track Connector — H-shaped bridge between two parallel rails.
 * One vertical leg is solid (active), the other leg + crossbar are dotted.
 */
import { LayoutElement, Position } from '../types';
import {
  ALL_RAIL_TYPES,
  getRailEndpoints,
  resolveRailType,
  type Pt,
} from './topology';

export type ConnectorShiftState = 'home' | 'shifted';

export const DEFAULT_CONNECTOR_LENGTH_METERS = 1;
export const DEFAULT_CONNECTOR_SHIFT_OFFSET_METERS = 1;
/** Gray H-connector — slightly cooler than silver rails for distinction. */
export const CONNECTOR_COLOR = '#9ca3af';
/** Slow transfer animation duration (ms). */
export const CONNECTOR_SHIFT_DURATION_MS = 2800;
/** How close an OHT must be to the solid leg to start a transfer (world units). */
export const CONNECTOR_SHIFT_TRIGGER_RADIUS = 72;

/** Rails closer than this (world units) are treated as the same lane. */
const SAME_LANE_EPS = 0.5;
/** Max angle difference (deg) for two rails to count as parallel. */
const PARALLEL_TOL_DEG = 12;
/** Ignore candidate rails farther than this (world units) when auto-snapping. */
const MAX_SHIFT_SEARCH_DIST = 500;

export function isConnectorElement(el: LayoutElement | null | undefined): boolean {
  if (!el) return false;
  return resolveRailType(el) === 'connector';
}

export function getConnectorShiftState(el: LayoutElement): ConnectorShiftState {
  return el.customProperties?.shiftState === 'shifted' ? 'shifted' : 'home';
}

export function getConnectorShiftOffsetMeters(el: LayoutElement): number {
  const raw = Number(el.customProperties?.shiftOffsetMeters);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_CONNECTOR_SHIFT_OFFSET_METERS;
}

/**
 * Unit vector along local +Y (lateral / H crossbar direction) in world space.
 */
export function getConnectorLateralUnit(el: LayoutElement): { x: number; y: number } {
  const r = ((el.rotation || 0) * Math.PI) / 180;
  return { x: -Math.sin(r), y: Math.cos(r) };
}

function areParallelRotations(aDeg: number, bDeg: number, tolDeg = PARALLEL_TOL_DEG): boolean {
  const d = Math.abs(aDeg - bDeg) % 180;
  const err = Math.min(d, 180 - d);
  return err <= tolDeg;
}

function isShiftTargetRail(el: LayoutElement): boolean {
  if (!ALL_RAIL_TYPES.has(el.type) || !el.metadata.visible) return false;
  return resolveRailType(el) === 'straight';
}

function projectOntoSegment(
  p: Pt,
  a: Pt,
  b: Pt
): { point: Pt; perpDist: number; t: number } {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len2 = vx * vx + vy * vy;
  if (len2 <= 1e-12) {
    return { point: { ...a }, perpDist: Math.hypot(p.x - a.x, p.y - a.y), t: 0 };
  }
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2));
  const point = { x: a.x + vx * t, y: a.y + vy * t };
  return { point, perpDist: Math.hypot(p.x - point.x, p.y - point.y), t };
}

function railCenterlineMid(el: LayoutElement): Pt {
  const ends = getRailEndpoints(el);
  if (ends.length >= 2) {
    return { x: (ends[0].x + ends[1].x) / 2, y: (ends[0].y + ends[1].y) / 2 };
  }
  return { x: el.position.x, y: el.position.y };
}

/**
 * Midpoint of the home leg (local y = 0) in world space.
 */
export function getConnectorHomeLegMid(el: LayoutElement): Pt {
  const w = el.dimensions.width;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return {
    x: el.position.x + (w / 2) * cos,
    y: el.position.y + (w / 2) * sin,
  };
}

/**
 * Midpoint of the far leg (local y = h) in world space.
 */
export function getConnectorFarLegMid(el: LayoutElement): Pt {
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  // local (w/2, h)
  return {
    x: el.position.x + (w / 2) * cos - h * sin,
    y: el.position.y + (w / 2) * sin + h * cos,
  };
}

/** Which leg is currently solid (active). */
export function getConnectorSolidLeg(el: LayoutElement): ConnectorShiftState {
  return getConnectorShiftState(el);
}

/** World midpoint of the currently solid leg. */
export function getConnectorSolidLegMid(el: LayoutElement): Pt {
  return getConnectorSolidLeg(el) === 'shifted'
    ? getConnectorFarLegMid(el)
    : getConnectorHomeLegMid(el);
}

/** World endpoints of the currently solid leg (along host rail). */
export function getConnectorSolidLegEndpoints(
  el: LayoutElement,
  solid: ConnectorShiftState = getConnectorSolidLeg(el)
): [Pt, Pt] {
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const toWorld = (lx: number, ly: number): Pt => ({
    x: el.position.x + lx * cos - ly * sin,
    y: el.position.y + lx * sin + ly * cos,
  });
  const ly = solid === 'shifted' ? h : 0;
  return [toWorld(0, ly), toWorld(w, ly)];
}

/**
 * Local Y of the solid (movable) leg for drawing.
 * 0 = home, h = far. Uses animation progress when provided (0..1 home→far).
 */
export function getConnectorSolidLegLocalY(
  el: LayoutElement,
  solidProgress01: number
): number {
  const h = Math.max(0, el.dimensions.height);
  return Math.max(0, Math.min(h, solidProgress01 * h));
}

/** Distance between the two legs (i.e. spacing of the bridged tracks). */
export function getConnectorSpan(el: LayoutElement): number {
  const home = getConnectorHomeLegMid(el);
  const far = getConnectorFarLegMid(el);
  return Math.hypot(far.x - home.x, far.y - home.y);
}

/**
 * Capture distance for a transfer. Always kept well under half the span so the
 * connector can never grab an OHT that is riding the opposite (dashed) track.
 */
export function getConnectorTriggerRadius(el: LayoutElement, requested?: number): number {
  const base = requested ?? CONNECTOR_SHIFT_TRIGGER_RADIUS;
  const span = getConnectorSpan(el);
  if (!(span > 0)) return base;
  return Math.max(2, Math.min(base, span * 0.35));
}

/** Distance an OHT must travel away before the same connector may grab it again. */
export function getConnectorReleaseRadius(el: LayoutElement): number {
  return getConnectorTriggerRadius(el) * 1.75;
}

/** World pose of the solid bar at progress 0 (home) → 1 (far). */
export function getConnectorSolidBarPose(
  el: LayoutElement,
  solidProgress01: number
): { x: number; y: number; angle: number } {
  const home = getConnectorHomeLegMid(el);
  const far = getConnectorFarLegMid(el);
  const t = Math.max(0, Math.min(1, solidProgress01));
  const x = home.x + (far.x - home.x) * t;
  const y = home.y + (far.y - home.y) * t;
  // Crossbar travel angle — for the solid bar only, never for OHT yaw.
  const angle = (Math.atan2(far.y - home.y, far.x - home.x) * 180) / Math.PI;
  return { x, y, angle };
}

/** Along-track heading (deg) of a connector leg (home or far). */
export function getConnectorLegHeadingDeg(
  el: LayoutElement,
  side: ConnectorShiftState
): number {
  const [a, b] = getConnectorSolidLegEndpoints(el, side);
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

/**
 * Snap a vehicle heading onto the connector leg axis (or 180° flip).
 * Keeps the OHT parallel to the host rails during wait/transfer/land — never
 * inherits the solid-bar crossbar angle.
 */
export function alignHeadingToConnectorLeg(
  el: LayoutElement,
  side: ConnectorShiftState,
  vehicleHeadingDeg?: number
): number {
  const leg = getConnectorLegHeadingDeg(el, side);
  if (vehicleHeadingDeg == null || !Number.isFinite(vehicleHeadingDeg)) return leg;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const hx = Math.cos(toRad(vehicleHeadingDeg));
  const hy = Math.sin(toRad(vehicleHeadingDeg));
  const lx = Math.cos(toRad(leg));
  const ly = Math.sin(toRad(leg));
  return hx * lx + hy * ly >= 0 ? leg : leg + 180;
}

export type ConnectorApproachHit = {
  connector: LayoutElement;
  /** Which leg the OHT is sitting on. */
  approachSide: ConnectorShiftState;
  /** True when the solid (red) bar is already on that leg. */
  isClosed: boolean;
  dist: number;
};

/**
 * True when the solid bar already covers `approachSide` (closed / connected).
 * Open = dashed/blurred on that side — OHT must wait for close first.
 */
export function isConnectorClosedForApproach(
  el: LayoutElement,
  approachSide: ConnectorShiftState
): boolean {
  return getConnectorShiftState(el) === approachSide;
}

/**
 * Find the nearest H-connector leg an OHT is approaching (home or far).
 * Unlike the old solid-only trigger, this detects open (dashed) approaches
 * so the sim can wait → close → then shift.
 *
 * When several connectors share the same rails (loop), prefer the leg that
 * lies ahead along the OHT heading — not only the smallest perpendicular
 * distance — so a second H further along the track can win over the one
 * just used.
 */
export function findConnectorApproachForOht(
  worldPos: Pt & { angle?: number },
  elements: LayoutElement[],
  options?: {
    triggerRadius?: number;
    isBusy?: (connectorId: string) => boolean;
    excludeIds?: Iterable<string>;
    headingDeg?: number;
  }
): ConnectorApproachHit | null {
  const radius = options?.triggerRadius ?? CONNECTOR_SHIFT_TRIGGER_RADIUS;
  const excluded = options?.excludeIds ? new Set(options.excludeIds) : null;
  const headingDeg =
    options?.headingDeg ??
    (typeof worldPos.angle === 'number' ? worldPos.angle : undefined);
  const headingRad =
    typeof headingDeg === 'number' ? (headingDeg * Math.PI) / 180 : null;
  const hx = headingRad != null ? Math.cos(headingRad) : 0;
  const hy = headingRad != null ? Math.sin(headingRad) : 0;

  let best: (ConnectorApproachHit & { score: number }) | null = null;

  for (const el of elements) {
    if (!isConnectorElement(el) || !el.metadata.visible) continue;
    if (excluded?.has(el.id)) continue;
    if (options?.isBusy?.(el.id)) continue;

    const effectiveRadius = getConnectorTriggerRadius(el, radius);
    const sides: ConnectorShiftState[] = ['home', 'shifted'];

    for (const side of sides) {
      const [a, b] = getConnectorSolidLegEndpoints(el, side);
      const { perpDist, t } = projectOntoSegment(worldPos, a, b);
      if (perpDist > effectiveRadius || t < -0.1 || t > 1.1) continue;

      const other: ConnectorShiftState = side === 'home' ? 'shifted' : 'home';
      const [oa, ob] = getConnectorSolidLegEndpoints(el, other);
      const otherDist = projectOntoSegment(worldPos, oa, ob).perpDist;
      // Must be genuinely on this leg, not the opposite one.
      if (perpDist >= otherDist) continue;

      const mid = side === 'shifted' ? getConnectorFarLegMid(el) : getConnectorHomeLegMid(el);
      const dx = mid.x - worldPos.x;
      const dy = mid.y - worldPos.y;
      const ahead = headingRad != null ? dx * hx + dy * hy : 0;
      // Prefer connectors ahead of travel; still allow slight behind if nearest.
      const aheadPenalty = headingRad != null && ahead < -8 ? 400 : 0;
      const score = perpDist + aheadPenalty - Math.max(0, ahead) * 0.35;

      if (!best || score < best.score) {
        best = {
          connector: el,
          approachSide: side,
          isClosed: isConnectorClosedForApproach(el, side),
          dist: perpDist,
          score,
        };
      }
    }
  }

  if (!best) return null;
  return {
    connector: best.connector,
    approachSide: best.approachSide,
    isClosed: best.isClosed,
    dist: best.dist,
  };
}

/**
 * If an OHT is on the solid (already closed) leg, return the transfer
 * home↔shifted. Prefer {@link findConnectorApproachForOht} for wait→close→shift.
 */
export function findConnectorShiftForOht(
  worldPos: Pt,
  elements: LayoutElement[],
  options?: { triggerRadius?: number; isBusy?: (connectorId: string) => boolean }
): { connector: LayoutElement; from: ConnectorShiftState; to: ConnectorShiftState } | null {
  const hit = findConnectorApproachForOht(worldPos, elements, options);
  if (!hit || !hit.isClosed) return null;
  return {
    connector: hit.connector,
    from: hit.approachSide,
    to: hit.approachSide === 'home' ? 'shifted' : 'home',
  };
}

export type NearestParallelRailHit = {
  rail: LayoutElement;
  /** World point on the target rail centerline. */
  point: Pt;
  perpDist: number;
};

/**
 * Find the nearest parallel straight rail to a world point (typically the home leg).
 */
export function findNearestParallelRailToPoint(
  origin: Pt,
  originRotationDeg: number,
  others: LayoutElement[],
  options?: { maxPerpDist?: number; minPerpDist?: number; excludeId?: string }
): NearestParallelRailHit | null {
  const maxPerpDist = options?.maxPerpDist ?? MAX_SHIFT_SEARCH_DIST;
  const minPerpDist = options?.minPerpDist ?? SAME_LANE_EPS;

  let best: NearestParallelRailHit | null = null;

  for (const other of others) {
    if (other.id === options?.excludeId || !isShiftTargetRail(other)) continue;
    if (!areParallelRotations(originRotationDeg, other.rotation || 0)) continue;

    const ends = getRailEndpoints(other);
    if (ends.length < 2) continue;

    const { point, perpDist } = projectOntoSegment(origin, ends[0], ends[1]);
    if (perpDist < minPerpDist || perpDist > maxPerpDist) continue;

    if (!best || perpDist < best.perpDist) {
      best = { rail: other, point, perpDist };
    }
  }

  return best;
}

/**
 * Find nearest parallel rail relative to the connector's home-leg midpoint.
 */
export function findNearestParallelRail(
  connector: LayoutElement,
  others: LayoutElement[],
  options?: { maxPerpDist?: number; minPerpDist?: number }
): NearestParallelRailHit | null {
  return findNearestParallelRailToPoint(
    getConnectorHomeLegMid(connector),
    connector.rotation || 0,
    others,
    { ...options, excludeId: connector.id }
  );
}

/**
 * Place an H-connector so its home leg sits on `host` and its far leg reaches
 * the nearest parallel rail (or a configured span if none found).
 */
export function fitConnectorToParallelRails(
  connector: LayoutElement,
  others: LayoutElement[],
  metersToWorld: (meters: number) => number
): { position: Position; rotation: number; dimensions: { width: number; height: number }; shiftOffsetMeters: number } {
  const width = Math.max(1, connector.dimensions.width);
  const host =
    others
      .filter(isShiftTargetRail)
      .map((rail) => {
        const ends = getRailEndpoints(rail);
        if (ends.length < 2) return null;
        const mid = railCenterlineMid(rail);
        // Prefer host whose centerline is closest to connector home leg / position.
        const probe = getConnectorHomeLegMid(connector);
        const { perpDist } = projectOntoSegment(probe, ends[0], ends[1]);
        return { rail, mid, perpDist, ends };
      })
      .filter((x): x is NonNullable<typeof x> => !!x)
      .sort((a, b) => a.perpDist - b.perpDist)[0] ?? null;

  const fallbackSpan = metersToWorld(getConnectorShiftOffsetMeters(connector));
  const lengthAlong = width;

  if (!host) {
    return {
      position: { ...connector.position },
      rotation: connector.rotation || 0,
      dimensions: { width: lengthAlong, height: Math.max(1, fallbackSpan) },
      shiftOffsetMeters: getConnectorShiftOffsetMeters(connector),
    };
  }

  // Anchor on host: project connector probe onto host centerline.
  const probe = getConnectorHomeLegMid(connector);
  const onHost = projectOntoSegment(probe, host.ends[0], host.ends[1]).point;

  const neighbor = findNearestParallelRailToPoint(
    onHost,
    host.rail.rotation || 0,
    others,
    { excludeId: host.rail.id }
  );

  const hostRot = host.rail.rotation || 0;
  let rotation = hostRot;
  // Prefer existing span so a failed neighbor lookup cannot shrink a good H.
  let span = Math.max(1, fallbackSpan, connector.dimensions.height || 0);
  let farPoint: Pt | null = null;

  if (neighbor) {
    span = Math.max(1, neighbor.perpDist);
    farPoint = neighbor.point;
    const delta = { x: neighbor.point.x - onHost.x, y: neighbor.point.y - onHost.y };
    const lat0 = { x: -Math.sin((hostRot * Math.PI) / 180), y: Math.cos((hostRot * Math.PI) / 180) };
    const dot = delta.x * lat0.x + delta.y * lat0.y;
    if (dot < 0) rotation = hostRot + 180;
  }

  const r = (rotation * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  // Home-leg midpoint local (w/2, 0) → world = position + R*(w/2, 0)
  const position: Position = {
    ...connector.position,
    x: onHost.x - (lengthAlong / 2) * cos,
    y: onHost.y - (lengthAlong / 2) * sin,
  };

  const oneMeter = metersToWorld(1);
  const shiftOffsetMeters =
    oneMeter > 0 ? Number((span / oneMeter).toFixed(3)) : getConnectorShiftOffsetMeters(connector);

  // If we have a far point, refine span from actual geometry after rotation choice.
  if (farPoint) {
    const lat = { x: -sin, y: cos };
    const signed = (farPoint.x - onHost.x) * lat.x + (farPoint.y - onHost.y) * lat.y;
    span = Math.max(1, Math.abs(signed));
  }

  return {
    position,
    rotation,
    dimensions: { width: lengthAlong, height: span },
    shiftOffsetMeters,
  };
}

/**
 * Build Redux updates for home ↔ shifted.
 * Only flips which leg is solid — never re-fits position/span (re-fit was
 * shrinking/moving the H after the first transfer).
 */
export function buildConnectorShiftUpdates(
  el: LayoutElement,
  nextState: ConnectorShiftState,
  _metersToWorld?: (meters: number) => number,
  _others: LayoutElement[] = []
): { id: string; updates: Partial<LayoutElement> } | null {
  const current = getConnectorShiftState(el);
  if (current === nextState) return null;

  return {
    id: el.id,
    updates: {
      customProperties: {
        ...(el.customProperties || {}),
        shiftState: nextState,
      },
    },
  };
}
